import React, { useCallback, useEffect, useRef, useState } from 'react';
import distributorService from '../../services/distributorService';
import useShippingRequest from '../../hooks/useShippingRequest';
import MailboxReviewForm from './MailboxReviewForm';

const unwrap = (response) => response?.data?.data;
const idPattern = /^[a-f\d]{24}$/i;
const revisionPattern = /^[a-f\d]{64}$/;
const statuses = ['pending', 'sending', 'sent', 'needs_review', 'cancelled'];
const statusLabels = {
  pending: 'Ready to send',
  sending: 'Sending — outcome unconfirmed',
  needs_review: 'Needs mailbox review',
};
const emptyReview = {
  id: '',
  expectedRevision: '',
  outcome: 'sent',
  messageId: '',
  reason: '',
  confirmedMailbox: false,
};
const emptyPending = { id: '', expectedRevision: '', confirmed: false };

export default function DistributorDeliveryQueue() {
  const [page, setPage] = useState(1);
  const [queue, setQueue] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState('');
  const busyRef = useRef(false);
  const [loading, setLoading] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [review, setReview] = useState(emptyReview);
  const [pendingReview, setPendingReview] = useState(emptyPending);
  const { begin, version } = useShippingRequest('distributor-deliveries');
  const load = useCallback(
    async (nextPage) => {
      if (busyRef.current) return;
      const request = begin();
      setLoading(true);
      setQueue(null);
      setReview(emptyReview);
      setPendingReview(emptyPending);
      try {
        const data = unwrap(
          await distributorService.adminDeliveryQueue(nextPage, { signal: request.signal })
        );
        if (
          !Array.isArray(data?.data) ||
          data.data.length > 20 ||
          !Number.isSafeInteger(data.total) ||
          data.total < 0 ||
          data.totalPages !== Math.ceil(data.total / 20) ||
          !Number.isSafeInteger(data.page) ||
          data.page < 1 ||
          data.page > Math.max(1, data.totalPages) ||
          typeof data.sendEnabled !== 'boolean' ||
          new Set(data.data.map((row) => row?._id)).size !== data.data.length ||
          data.data.some(
            (row) =>
              !idPattern.test(row?._id) ||
              !Number.isSafeInteger(row.formIndex) ||
              row.formIndex < 0 ||
              !['pending', 'sending', 'needs_review'].includes(row.status) ||
              !revisionPattern.test(row.reviewRevision) ||
              (row.recordedSendReference != null &&
                !/^[A-Za-z0-9._:-]{6,200}$/.test(row.recordedSendReference)) ||
              typeof row.canRetry !== 'boolean' ||
              typeof row.canReconcile !== 'boolean' ||
              (row.canRetry && (row.status !== 'pending' || !data.sendEnabled)) ||
              (row.canReconcile && !['sending', 'needs_review'].includes(row.status))
          )
        )
          throw new Error('Invalid email queue');
        if (!request.isCurrent()) return;
        setQueue(data);
        setPage(data.page);
        setUncertain(false);
        setError((current) =>
          current
            ? 'Queue refreshed. Check the current status and sending mailbox before another attempt.'
            : ''
        );
      } catch {
        if (request.isCurrent()) setError('BLS order email queue is unavailable. Please retry.');
      } finally {
        if (request.isCurrent()) setLoading(false);
      }
    },
    [begin]
  );
  useEffect(() => {
    busyRef.current = false;
    setBusy('');
    setError('');
    setNotice('');
    setUncertain(false);
    setReview(emptyReview);
    setPendingReview(emptyPending);
    load(1);
  }, [load, version]);
  const retry = async (event) => {
    event.preventDefault();
    const captured = { ...pendingReview };
    const row = queue?.data.find((item) => item._id === captured.id);
    if (
      busyRef.current ||
      loading ||
      uncertain ||
      !captured.confirmed ||
      !row?.canRetry ||
      row.reviewRevision !== captured.expectedRevision ||
      !queue.sendEnabled
    )
      return;
    busyRef.current = true;
    const request = begin();
    setBusy(captured.id);
    setError('');
    setNotice('');
    try {
      const receipt = unwrap(
        await distributorService.retryPendingDelivery(captured.id, {
          expectedRevision: captured.expectedRevision,
          confirm: true,
        })
      );
      if (!statuses.includes(receipt?.status)) throw new Error('Invalid email receipt');
      if (!request.isCurrent()) return;
      setNotice(
        receipt.status === 'sent'
          ? 'The sending service accepted the order email. This does not confirm shipment or delivery.'
          : 'The attempt is recorded. Check the refreshed status and sending mailbox before further action.'
      );
      busyRef.current = false;
      setBusy('');
      await load(page);
    } catch {
      if (request.isCurrent()) {
        setQueue(null);
        setUncertain(true);
        setError(
          'Order email was not confirmed. Reload the queue and review the sending mailbox before retrying.'
        );
      }
    } finally {
      if (request.isCurrent()) {
        busyRef.current = false;
        setBusy('');
      }
    }
  };
  const reconcile = async (event) => {
    event.preventDefault();
    const captured = { ...review };
    const row = queue?.data.find((item) => item._id === captured.id);
    if (
      busyRef.current ||
      loading ||
      uncertain ||
      !row?.canReconcile ||
      row.reviewRevision !== captured.expectedRevision ||
      !captured.confirmedMailbox ||
      captured.reason.trim().length < 12 ||
      captured.reason.length > 300 ||
      !['sent', 'not_sent'].includes(captured.outcome) ||
      (captured.outcome === 'sent' && !/^[A-Za-z0-9._:-]{6,200}$/.test(captured.messageId))
    )
      return;
    busyRef.current = true;
    const request = begin();
    setBusy(captured.id);
    setError('');
    setNotice('');
    try {
      const receipt = unwrap(
        await distributorService.reconcileDelivery(captured.id, {
          expectedRevision: captured.expectedRevision,
          outcome: captured.outcome,
          reason: captured.reason,
          confirmedMailbox: true,
          ...(captured.outcome === 'sent' ? { vendorMessageId: captured.messageId } : {}),
        })
      );
      if (receipt?.status !== (captured.outcome === 'sent' ? 'sent' : 'pending'))
        throw new Error('Invalid review receipt');
      if (!request.isCurrent()) return;
      setNotice(
        captured.outcome === 'sent'
          ? 'Mailbox review saved. No email was sent by this review.'
          : 'Mailbox review saved. The form is ready for a separate, explicitly authorized send. No email was sent by this review.'
      );
      busyRef.current = false;
      setBusy('');
      await load(page);
    } catch {
      if (request.isCurrent()) {
        setQueue(null);
        setUncertain(true);
        setError('Mailbox review was not confirmed. Reload the queue before trying again.');
      }
    } finally {
      if (request.isCurrent()) {
        busyRef.current = false;
        setBusy('');
      }
    }
  };
  return (
    <section
      className="me-distributor-admin-card me-distributor-admin-queue space-y-4"
      aria-label="BLS order email recovery"
    >
      <h2 className="text-lg font-semibold text-slate-900">BLS order email recovery</h2>
      {queue && !queue.sendEnabled && (
        <p role="status">Order email sending is disabled. Mailbox reviews can still be recorded.</p>
      )}
      {error && (
        <p role="alert" className="text-red-700">
          {error}
        </p>
      )}
      {notice && (
        <p role="status" className="text-green-700">
          {notice}
        </p>
      )}
      <button
        type="button"
        disabled={loading || !!busy}
        className="me-distributor-admin-secondary"
        onClick={() => load(page)}
      >
        Refresh order email queue
      </button>
      {loading && <p role="status">Loading order emails...</p>}
      {queue?.data.length === 0 && <p>No order emails need recovery.</p>}
      {queue?.data.map((row) => (
        <div key={row._id} className="me-distributor-admin-queue-row space-y-2">
          <p>
            <strong>{row.orderId || 'Order unavailable'}</strong> · form {row.formIndex + 1} ·{' '}
            {statusLabels[row.status]}
          </p>
          {row.status === 'pending' && row.canRetry && (
            <button
              type="button"
              disabled={!!busy || loading}
              onClick={() => {
                setReview(emptyReview);
                setPendingReview({
                  id: row._id,
                  expectedRevision: row.reviewRevision,
                  confirmed: false,
                });
              }}
              className="me-distributor-admin-primary"
            >
              Review pending send
            </button>
          )}
          {row.status === 'pending' && !row.canRetry && (
            <p className="text-sm text-slate-600">
              Sending is unavailable. Check the order status and sending configuration.
            </p>
          )}
          {pendingReview.id === row._id && (
            <form onSubmit={retry} className="space-y-3">
              <p className="text-sm text-slate-600">
                Sending this form asks BLS to fulfill an order. Confirm it has not already been
                sent.
              </p>
              <label className="me-distributor-admin-confirm flex items-start gap-2">
                <input
                  type="checkbox"
                  disabled={!!busy}
                  checked={pendingReview.confirmed}
                  onChange={(event) =>
                    setPendingReview((current) => ({ ...current, confirmed: event.target.checked }))
                  }
                />
                I reviewed this order and authorize sending its BLS form
              </label>
              <div className="flex flex-wrap gap-3">
                <button
                  type="submit"
                  disabled={!!busy || !pendingReview.confirmed}
                  className="me-distributor-admin-primary"
                >
                  Send reviewed form
                </button>
                <button
                  type="button"
                  disabled={!!busy}
                  onClick={() => setPendingReview(emptyPending)}
                  className="me-distributor-admin-secondary"
                >
                  Cancel send
                </button>
              </div>
            </form>
          )}
          {['sending', 'needs_review'].includes(row.status) && !row.canReconcile && (
            <p className="text-sm text-slate-600">
              An active or incomplete attempt cannot be reviewed yet. Wait at least 15 minutes, then
              refresh. If its claim time is missing, ask MedEffects support.
            </p>
          )}
          {row.canReconcile && (
            <button
              type="button"
              disabled={!!busy || loading}
              className="me-distributor-admin-text-button"
              onClick={() => {
                setPendingReview(emptyPending);
                setReview({ ...emptyReview, id: row._id, expectedRevision: row.reviewRevision });
              }}
            >
              Record mailbox review
            </button>
          )}
          {review.id === row._id && (
            <div className="space-y-3">
              {row.recordedSendReference ? (
                <p className="text-sm text-slate-600">
                  Recorded transport reference (not mailbox proof):{' '}
                  <code className="break-all">{row.recordedSendReference}</code>
                </p>
              ) : (
                <p className="text-sm text-slate-600">
                  No unambiguous recorded sent reference is available. If you cannot independently
                  verify this attempt, leave it for MedEffects support.
                </p>
              )}
              <MailboxReviewForm
                originalSend
                review={review}
                setReview={setReview}
                disabled={!!busy || loading}
                onSubmit={reconcile}
                onCancel={() => setReview(emptyReview)}
              />
            </div>
          )}
        </div>
      ))}
      {queue?.totalPages > 1 && (
        <div className="me-distributor-admin-pagination flex items-center gap-3">
          <button
            type="button"
            className="me-distributor-admin-secondary"
            disabled={loading || !!busy || page === 1}
            onClick={() => load(page - 1)}
          >
            Previous email page
          </button>
          <span>
            Page {page} of {queue.totalPages}
          </span>
          <button
            type="button"
            className="me-distributor-admin-secondary"
            disabled={loading || !!busy || page >= queue.totalPages}
            onClick={() => load(page + 1)}
          >
            Next email page
          </button>
        </div>
      )}
    </section>
  );
}
